/**
 * 等角 (isometric) 座標変換ユーティリティ。
 *
 * 論理タイル: 16×16 px（グリッド単位）
 * 画面投影は 2:1 菱形 (diamond) ＝ width 32 × height 16 px。
 * マップ 20×20 タイルで、投影後の外接矩形は 640×320 px になる。
 *
 * 描画時は PixiStage 側で devicePixelRatio と zoom を掛けて表示する。
 */

/** 論理タイルサイズ（1タイルの一辺の長さ、px） */
export const TILE_SIZE = 16;

/** 等角菱形タイルの表示幅 = TILE_SIZE * 2 */
export const ISO_TILE_W = TILE_SIZE * 2;

/** 等角菱形タイルの表示高さ = TILE_SIZE * 1 */
export const ISO_TILE_H = TILE_SIZE;

/** マップのタイル数（列・行） */
export const MAP_COLS = 20;
export const MAP_ROWS = 20;

export interface TilePos {
  readonly col: number;
  readonly row: number;
}

export interface ScreenPos {
  readonly x: number;
  readonly y: number;
}

/**
 * タイル座標（col, row）を等角スクリーン座標に変換。
 * 原点 (0, 0) タイルの中心は (0, 0) スクリーンに来る。
 * 小数の col/row も受け付ける（アニメーションで補間可能）。
 */
export function tileToScreen(col: number, row: number): ScreenPos {
  return {
    x: (col - row) * (ISO_TILE_W / 2),
    y: (col + row) * (ISO_TILE_H / 2),
  };
}

/**
 * スクリーン座標をタイル座標に逆変換（小数）。
 * クリック時のタイル特定などに使う。
 */
export function screenToTile(x: number, y: number): TilePos {
  const col = x / ISO_TILE_W + y / ISO_TILE_H;
  const row = y / ISO_TILE_H - x / ISO_TILE_W;
  return { col, row };
}

/**
 * Zソート用の深度値。y座標が大きいほど手前。
 * 等角では (col + row) が大きいほど手前にあるので、それをそのまま使う。
 */
export function depthOf(col: number, row: number): number {
  return col + row;
}

/**
 * マップ全体の投影サイズ（ビューポートの中央寄せに使用）。
 */
export function getMapBounds(): { width: number; height: number } {
  return {
    width: (MAP_COLS + MAP_ROWS) * (ISO_TILE_W / 2),
    height: (MAP_COLS + MAP_ROWS) * (ISO_TILE_H / 2),
  };
}
